<template>
    <el-dialog
        v-model="visible"
        :title="isEdit ? '编辑计费方式' : '添加计费方式'"
        width="700px"
        append-to-body
        :close-on-click-modal="false"
        @close="handleClose"
    >
        <el-form ref="formRef" :model="form" :rules="rules" label-width="120px" label-position="right" style="padding: 10px 0">
            <!-- 计费方式 -->
            <el-form-item label="计费方式" prop="method">
                <el-radio-group v-model="form.method" @change="handleMethodChange">
                    <el-radio label="1">热量表</el-radio>
                    <el-radio label="2">热量表+温控器</el-radio>
                    <el-radio label="3">温控器</el-radio>
                </el-radio-group>
            </el-form-item>

            <!-- 热量表选择（计费方式含热量表时显示） -->
            <el-form-item label="热量表" prop="heat_meter" v-if="form.method === '1' || form.method === '2'">
                <el-select v-model="form.heat_meter" placeholder="请选择热量表" style="width: 100%" filterable clearable>
                    <el-option
                        v-for="item in heatMeterList"
                        :key="item.id"
                        :label="item.name || item.IMEI || item.id"
                        :value="item.id"
                    />
                </el-select>
                <!-- <div class="form-tip">当计费方式含热量表时，需要设置总表</div> -->
            </el-form-item>

            <!-- 热量表单价（计费方式含热量表时显示） -->
            <el-form-item label="热量表单价" prop="heat_unit_price" v-if="form.method === '1' || form.method === '2'">
                <el-input-number
                    v-model="form.heat_unit_price"
                    :precision="2"
                    :min="0"
                    placeholder="请输入热量单价"
                    style="width: 200px"
                />
                <span class="unit">元/千瓦时</span>
            </el-form-item>

            <!-- 温控器选择（计费方式含温控器时显示） -->
            <el-form-item label="温控器" prop="thermostat" v-if="form.method === '2' || form.method === '3'">
                <el-select
                    v-model="form.thermostat"
                    placeholder="请选择温控器（小区主控）"
                    style="width: 100%"
                    filterable
                    clearable
                >
                    <el-option
                        v-for="item in thermostatList"
                        :key="item.id"
                        :label="item.name || item.IMEI || item.id"
                        :value="item.id"
                    />
                </el-select>
                <!-- <div class="form-tip">当计费方式含温控器时必须选择小区主控</div> -->
            </el-form-item>

            <!-- 功率单价（计费方式含温控器时显示） -->
            <template v-if="form.method === '2' || form.method === '3'">
                <el-form-item label="运行时单价" prop="unit_price">
                    <el-input-number
                        v-model="form.unit_price"
                        :precision="2"
                        :min="0"
                        placeholder="请输入运行时单价"
                        style="width: 200px"
                    />
                    <span class="unit">元/小时</span>
                </el-form-item>
            </template>

            <!-- 基础费用 -->
            <el-form-item label="基础费用" prop="basic_fee">
                <el-input-number
                    v-model="form.basic_fee"
                    :precision="2"
                    :min="0"
                    placeholder="请输入基础费用"
                    style="width: 200px"
                />
                <span class="unit">元</span>
            </el-form-item>

            <!-- 按钮 -->
            <el-form-item style="text-align: center; margin-top: 20px">
                <el-button type="primary" @click="submit" :loading="submitLoading">提交</el-button>
                <el-button @click="handleClose">取消</el-button>
            </el-form-item>
        </el-form>
    </el-dialog>
</template>

<script setup>
import { ref, reactive, watch } from 'vue'
import { ElMessage } from 'element-plus'
import { addBilling, editBilling, getBillingDetail } from '@/api/thermostat/billing.js'
import { getDeviceList as getHeatMeterList } from '@/api/heat_meter/devicePoint.js'
import { getDeviceList as getThermostatList } from '@/api/thermostat/devicePoint.js'

const emit = defineEmits(['success'])

const visible = ref(false)
const formRef = ref(null)
const submitLoading = ref(false)
const isEdit = ref(false)
const currentBuilding = ref('')

// 热量表列表
const heatMeterList = ref([])
// 温控器列表
const thermostatList = ref([])

const form = reactive({
    id: '',
    building: '',
    method: '1',
    heat_meter: '',
    heat_unit_price: '',
    thermostat: '',
    unit_price: '',
    basic_fee: ''
})

// 表单验证规则
const rules = {
    method: [{ required: true, message: '请选择计费方式', trigger: 'change' }],
    heat_meter: [
        {
            required: true,
            message: '请选择热量表',
            trigger: 'change',
            validator: (rule, value, callback) => {
                if ((form.method === '1' || form.method === '2') && !value) {
                    callback(new Error('请选择热量表'))
                } else {
                    callback()
                }
            }
        }
    ],
    heat_unit_price: [
        {
            required: true,
            message: '请输入热量表单价',
            trigger: 'blur',
            validator: (rule, value, callback) => {
                if ((form.method === '1' || form.method === '2') && !value && value !== 0) {
                    callback(new Error('请输入热量表单价'))
                } else {
                    callback()
                }
            }
        }
    ],
    // thermostat: [
    //     {
    //         required: true,
    //         message: '请选择温控器',
    //         trigger: 'change',
    //         validator: (rule, value, callback) => {
    //             if ((form.method === '2' || form.method === '3') && !value) {
    //                 callback(new Error('请选择温控器'))
    //             } else {
    //                 callback()
    //             }
    //         }
    //     }
    // ],
    unit_price: [
        {
            required: true,
            message: '请输入单价',
            trigger: 'blur',
            validator: (rule, value, callback) => {
                if ((form.method === '2' || form.method === '3') && !value && value !== 0) {
                    callback(new Error('请输入运行时单价'))
                } else {
                    callback()
                }
            }
        }
    ],
    basic_fee: [{ required: true, message: '请输入基础费用', trigger: 'blur' }]
}

// 获取热量表列表
async function loadHeatMeterList(building) {
    if (!building) return
    try {
        const res = await getHeatMeterList({ building, page: 1, page_size: 999 })
        heatMeterList.value = res?.lists || []
    } catch (err) {
        console.log('获取热量表列表失败', err)
        heatMeterList.value = []
    }
}

// 获取温控器列表
async function loadThermostatList(building) {
    if (!building) return
    try {
        const res = await getThermostatList({ building, page: 1, page_size: 999 })
        thermostatList.value = res?.lists || []
    } catch (err) {
        console.log('获取温控器列表失败', err)
        thermostatList.value = []
    }
}

// 计费方式改变时，清空相关字段
function handleMethodChange(val) {
    if (val === '1') {
        // 仅热量表：清空温控器相关
        form.thermostat = ''
        form.unit_price = ''
    } else if (val === '3') {
        // 仅温控器：清空热量表相关
        form.heat_meter = ''
        form.heat_unit_price = ''
    }
    // method=2 时保留所有字段
}

// 打开弹窗
async function open(data = {}) {
    visible.value = true
    isEdit.value = !!data.id
    currentBuilding.value = data.building || ''

    // 重置表单
    reset()

    // 加载设备列表
    if (currentBuilding.value) {
        await loadHeatMeterList(currentBuilding.value)
        await loadThermostatList(currentBuilding.value)
    }

    // 编辑模式：填充数据
    if (isEdit.value && data.id) {
        try {
            const detail = await getBillingDetail(data.building)
            if (detail) {
                Object.assign(form, {
                    id: detail.id || data.id,
                    building: detail.building || data.building,
                    method: String(detail.method || '1'),
                    heat_meter: detail.heat_meter || '',
                    heat_unit_price: detail.heat_unit_price || '',
                    thermostat: detail.thermostat || '',
                    unit_price: detail.unit_price || '',
                    basic_fee: detail.basic_fee || ''
                })
            }
        } catch (err) {
            console.log('获取详情失败', err)
            // 使用传入的数据作为回退
            Object.assign(form, {
                id: data.id,
                building: data.building,
                method: String(data.method || '1'),
                heat_meter: data.heat_meter || '',
                heat_unit_price: data.heat_unit_price || '',
                thermostat: data.thermostat || '',
                unit_price: data.unit_price || '',
                basic_fee: data.basic_fee || ''
            })
        }
    } else {
        // 新增模式
        form.building = currentBuilding.value
    }
}

// 关闭弹窗
function handleClose() {
    visible.value = false
    reset()
}

// 重置
function reset() {
    if (formRef.value) {
        formRef.value.resetFields()
        formRef.value.clearValidate()
    }

    form.id = ''
    form.building = currentBuilding.value
    form.method = '1'
    form.heat_meter = ''
    form.heat_unit_price = ''
    form.thermostat = ''
    form.unit_price = ''
    form.basic_fee = ''

    heatMeterList.value = []
    thermostatList.value = []
}

// 提交
async function submit() {
    if (!formRef.value) return

    const valid = await formRef.value.validate().catch(() => false)
    if (!valid) return

    submitLoading.value = true

    // 构建提交数据
    const submitData = {
        building: form.building,
        method: form.method,
        basic_fee: String(form.basic_fee)
    }

    // 根据计费方式添加对应字段
    if (form.method === '1' || form.method === '2') {
        submitData.heat_meter = form.heat_meter
        submitData.heat_unit_price = String(form.heat_unit_price)
    }

    if (form.method === '2' || form.method === '3') {
        if (form.thermostat) {
            submitData.thermostat = form.thermostat
        }
        submitData.unit_price = String(form.unit_price)
    }

    // 编辑模式添加id
    if (isEdit.value && form.id) {
        submitData.id = form.id
    }

    try {
        if (isEdit.value) {
            await editBilling(submitData)
            ElMessage.success('编辑成功')
        } else {
            await addBilling(submitData)
            ElMessage.success('添加成功')
        }
        visible.value = false
        emit('success')
        reset()
    } catch (err) {
        console.log('提交失败', err)
        ElMessage.error(err?.message || '操作失败')
    } finally {
        submitLoading.value = false
    }
}

defineExpose({ open })
</script>

<style scoped>
.unit {
    margin-left: 8px;
    color: #606266;
    font-size: 14px;
}
.form-tip {
    font-size: 12px;
    color: #909399;
    margin-top: 4px;
    line-height: 1.4;
}
</style>
